/**
 * The rows of one file, for reviewing client data.
 *
 * ONE component for both file pages — the intake page (the file's own rows) and
 * the audit's file page (the rows locked into the audit). Only the base path
 * differs. Everything is computed on the server: paging, counting, search,
 * sort, the values a filter offers and the Excel export. The browser holds one
 * page at a time and never builds the workbook.
 *
 * Order on the page: figures, an optional section the page supplies
 * (classification, on intake), the toolbar, the table, the pager.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Box, Button, Chip } from '@mui/material';
import {
	Card,
	Detail,
	Empty,
	Note,
	SideDrawer,
	StatusBadge,
	humanise,
} from '../../shared/ui';
import { ApiError } from '../../api/errors';
import { fileRowsBase } from '../../api/FileRows';
import { useAuth } from '../../provider/auth';
import { useToast } from '../../provider/toast';
import { useCanonicalFieldsQuery } from '../../queries/firm';
import {
	useExportFileRowsMutation,
	useFileRowsQuery,
	useFileRowsSummaryQuery,
	useFileRowValuesQuery,
} from '../../queries/fileRows';
import { count, date, money, quantity } from '../../utils/format';
import { Figure, Figures } from '../shared/badges';
import { EmptyState } from '../shared/states';
import {
	ColumnVisibility,
	DataTable,
	FilterBar,
	useFullScreen,
	type Column,
	type FilterSpec,
	type ServerSort,
} from '../shared/tables';
import type {
	FileRecord,
	FileRowCell,
	FileRowColumn,
	FileRowFilters,
	FileRowsSummary,
} from '../../schemas';

const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DELAY_MS = 300;
/** Above this many rows the download takes long enough to warn about. */
const LARGE_DOWNLOAD_ROWS = 20_000;

/** Never shown to anyone: an internal reference, not something an auditor reads. */
const HIDDEN_KEY = 'file_id';
const SOURCE_ROW_KEY = 'source_row_number';
const CLASSIFICATION_KEY = 'classification';

/** Reading order for the classification figures; anything else follows A–Z. */
const CLASS_ORDER = [
	'SALES',
	'RETURNS',
	'ADJUSTMENT',
	'ADJUSTMENTS',
	'EXCLUDED',
];
const UNCLASSIFIED = 'UNCLASSIFIED';

/* ═══ small helpers ════════════════════════════════════════════════════════ */

/** A value that settles after the typing stops. */
function useDebounced<T>(value: T, delay = SEARCH_DELAY_MS): T {
	const [settled, setSettled] = useState(value);
	useEffect(() => {
		const timer = setTimeout(() => setSettled(value), delay);
		return () => clearTimeout(timer);
	}, [value, delay]);
	return settled;
}

function isEmpty(cell: FileRowCell | undefined): boolean {
	return cell === null || cell === undefined || cell === '';
}

function Missing() {
	return <span style={{ color: 'var(--ink-3)' }}>—</span>;
}

/** Text the file holds as blank — a fact about the data, not a missing value. */
function Blank() {
	return (
		<span style={{ color: 'var(--ink-3)', fontStyle: 'italic' }}>
			(blank)
		</span>
	);
}

/** Long text wraps to two lines, then ends in an ellipsis; the full value is
 *  on hover and in the row details (08 §5). */
function Clamped({ text }: { text: string }) {
	return (
		<span
			title={text}
			style={{
				display: '-webkit-box',
				WebkitLineClamp: 2,
				WebkitBoxOrient: 'vertical',
				overflow: 'hidden',
			}}
		>
			{text}
		</span>
	);
}

/** One cell in the way its column's kind is read. */
function formatCell(
	cell: FileRowCell | undefined,
	type: FileRowColumn['type'],
): ReactNode {
	if (cell === '') return <Blank />;
	if (isEmpty(cell)) return <Missing />;
	switch (type) {
		case 'money':
			return money(cell, true);
		case 'decimal':
			return quantity(cell);
		case 'integer':
			return count(cell);
		case 'date':
			return date(cell);
		default:
			return String(cell);
	}
}

/** "Period · Rows · File groups · Sheet" — only the parts the file really has. */
export function fileMetaLine(file: FileRecord): string {
	const period =
		file.periodLabel ??
		(file.periodStart && file.periodEnd
			? `${date(file.periodStart)} to ${date(file.periodEnd)}`
			: null);
	const groups = file.fileGroups?.map((group) => group.name) ?? [];
	return [
		period,
		file.rowCount !== null ? `${count(file.rowCount)} rows` : null,
		groups.length > 0
			? `${groups.length === 1 ? 'File group' : 'File groups'}: ${groups.join(', ')}`
			: null,
		file.sheetName ? `Sheet: ${file.sheetName}` : null,
	]
		.filter(Boolean)
		.join(' | ');
}

/** The classification figures' own order, with unclassified last. */
function classificationEntries(byClassification: Record<string, number>) {
	const rank = (key: string) => {
		if (key === UNCLASSIFIED) return 1000;
		const index = CLASS_ORDER.indexOf(key);
		return index === -1 ? 500 : index;
	};
	return (
		Object.entries(byClassification)
			/* Unclassified stays even at 0: "nothing unclassified" is the fact a
       reviewer checks first. Other classes with no rows are left out. */
			.filter(([key, value]) => value > 0 || key === UNCLASSIFIED)
			.sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
	);
}

/* Which group of the row details a standard field sits in. Anything not named
   here is placed by what kind of value it holds, and the rest goes in Other. */
const GROUP_OF: Record<string, string> = {
	invoice_number: 'Invoice',
	invoice_series: 'Invoice',
	document_type: 'Invoice',
	document_type_desc: 'Invoice',
	sd_document_number: 'Invoice',
	accounting_document: 'Invoice',
	reference_invoice: 'Invoice',
	billed_month: 'Invoice',
	so_number: 'Invoice',
	payment_terms: 'Invoice',
	payment_terms_desc: 'Invoice',
	customer_id: 'Customer',
	customer_name: 'Customer',
	customer_gstin: 'Customer',
	customer_location: 'Customer',
	delivery_location: 'Customer',
	state_code: 'Customer',
	state_name: 'Customer',
	material_id: 'Material',
	material_desc: 'Material',
	batch_number: 'Material',
	pack_size: 'Material',
	billed_quantity: 'Material',
	free_quantity: 'Material',
	order_quantity: 'Material',
	division: 'Material',
	division_desc: 'Material',
	current_division: 'Material',
	current_division_desc: 'Material',
};
const GROUP_ORDER = [
	'Invoice',
	'Customer',
	'Material',
	'Amounts',
	'Dates',
	'Other',
];

function groupOf(key: string, type: FileRowColumn['type']): string {
	const named = GROUP_OF[key];
	if (named) return named;
	if (type === 'money' || type === 'decimal') return 'Amounts';
	if (type === 'date') return 'Dates';
	return 'Other';
}

type PageRow = { index: number; cells: FileRowCell[] };

/* ═══ the workbench ════════════════════════════════════════════════════════ */

export function FileDataWorkbench({
	file,
	auditId,
	between,
}: {
	file: FileRecord;
	/** Present on the audit side: reads the rows locked into that audit. */
	auditId?: string;
	/** A section the page places between the figures and the toolbar. */
	between?: (summary: FileRowsSummary | undefined) => ReactNode;
}) {
	const base = fileRowsBase(file.pkFileId, auditId);
	const fields = useCanonicalFieldsQuery(file.module);
	/* The full-screen control lives in the toolbar rather than on a bar of its
     own above the table — one row of chrome fewer at 1366×768. */
	const full = useFullScreen(`${file.originalName} — rows`);
	const { vocabulary } = useAuth();
	const toast = useToast();

	/* The server answers 404 until the bytes have been read into rows, so do not
     ask before that — an honest "not processed yet" beats an error. */
	/* On the audit side the server decides (the rows locked into the audit stay
     readable even if the file has since been excluded), and an auditor is not
     sent the file's storage details to guess from. */
	const processed =
		Boolean(auditId) ||
		file.status === 'READY' ||
		Boolean(file.s3NormalisedParquetKey);
	const summary = useFileRowsSummaryQuery(base, processed);

	const labelOf = useMemo(() => {
		const byKey = new Map(
			(fields.data?.fields ?? []).map((f) => [f.field, f.label]),
		);
		return (key: string) => {
			if (key === SOURCE_ROW_KEY) return 'Row in source file';
			if (key === CLASSIFICATION_KEY) return 'Classification';
			return byKey.get(key) ?? humanise(key);
		};
	}, [fields.data]);
	const classLabel = (value: string) =>
		vocabulary?.classification?.[value]?.label ?? humanise(value);

	/* ── what is asked for ── */
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [sort, setSort] = useState<ServerSort>(null);
	const [searchText, setSearchText] = useState('');
	const q = useDebounced(searchText.trim());
	const [chosen, setChosen] = useState({
		classification: '',
		plantCode: '',
		documentType: '',
		periodStart: '',
		periodEnd: '',
	});
	const [plantText, setPlantText] = useState('');
	const [documentText, setDocumentText] = useState('');
	const plantSearch = useDebounced(plantText.trim());
	const documentSearch = useDebounced(documentText.trim());

	/* A new question starts at the first page. */
	useEffect(() => setPage(1), [q]);

	const filters: FileRowFilters = {
		q: q || undefined,
		classification: chosen.classification || undefined,
		plantCode: chosen.plantCode || undefined,
		documentType: chosen.documentType || undefined,
		periodStart: chosen.periodStart || undefined,
		periodEnd: chosen.periodEnd || undefined,
	};
	const filtering = Object.values(filters).some(Boolean);

	const summaryData = summary.data;
	const ready = Boolean(summaryData);
	const rows = useFileRowsQuery(
		base,
		{
			page,
			pageSize,
			...filters,
			sort: sort?.key,
			dir: sort ? sort.direction : undefined,
		},
		ready,
	);

	/* ── the values each filter offers ── */
	const filterable = new Set(summaryData?.filterable ?? []);
	const scope = {
		classification: chosen.classification || undefined,
		plantCode: chosen.plantCode || undefined,
		documentType: chosen.documentType || undefined,
		periodStart: chosen.periodStart || undefined,
		periodEnd: chosen.periodEnd || undefined,
	};
	const classValues = useFileRowValuesQuery(
		base,
		{ column: CLASSIFICATION_KEY, ...scope, limit: 50 },
		ready && filterable.has(CLASSIFICATION_KEY),
	);
	const plantValues = useFileRowValuesQuery(
		base,
		{
			column: 'plant_code',
			...scope,
			q: plantSearch || undefined,
			limit: 50,
		},
		ready && filterable.has('plant_code'),
	);
	const documentValues = useFileRowValuesQuery(
		base,
		{
			column: 'document_type',
			...scope,
			q: documentSearch || undefined,
			limit: 50,
		},
		ready && filterable.has('document_type'),
	);

	/* ── columns ── */
	const allColumns = useMemo(
		() =>
			(summaryData?.columns ?? []).filter(
				(column) => column.key !== HIDDEN_KEY,
			),
		[summaryData],
	);
	const defaultVisible = useMemo(
		() => allColumns.map((column) => column.key),
		[allColumns],
	);
	const [visibleKeys, setVisibleKeys] = useState<string[] | null>(null);
	const visible = visibleKeys ?? defaultVisible;

	const [open, setOpen] = useState<PageRow | null>(null);
	const offset =
		((rows.data?.page ?? page) - 1) * (rows.data?.pageSize ?? pageSize);

	const positionOf = useMemo(
		() =>
			new Map(
				(rows.data?.columns ?? []).map((column, index) => [
					column.key,
					index,
				]),
			),
		[rows.data],
	);

	const tableColumns: Array<Column<PageRow>> = useMemo(() => {
		const shown = new Set(visible);
		return allColumns
			.filter((column) => shown.has(column.key))
			.map((column): Column<PageRow> => {
				const numeric =
					column.type === 'money' ||
					column.type === 'decimal' ||
					column.type === 'integer' ||
					column.key === SOURCE_ROW_KEY;
				return {
					key: column.key,
					label: labelOf(column.key),
					num: numeric,
					sortable: true,
					width:
						column.key === SOURCE_ROW_KEY
							? 140
							: column.key === CLASSIFICATION_KEY
								? 150
								: numeric
									? 140
									: column.type === 'date'
										? 120
										: 180,
					render: (row) => {
						const cell =
							row.cells[positionOf.get(column.key) ?? -1];
						if (column.key === CLASSIFICATION_KEY) {
							return isEmpty(cell) ? (
								<StatusBadge
									group="classification"
									value={UNCLASSIFIED}
								/>
							) : (
								<StatusBadge
									group="classification"
									value={String(cell)}
								/>
							);
						}
						if (column.key === SOURCE_ROW_KEY) {
							return isEmpty(cell) ? <Missing /> : count(cell);
						}
						return column.type === 'text' && !isEmpty(cell) ? (
							<Clamped text={String(cell)} />
						) : (
							formatCell(cell, column.type)
						);
					},
				};
			});
	}, [allColumns, visible, labelOf, positionOf]);

	/* ── the export ── */
	const exportRows = useExportFileRowsMutation(base);
	const download = () =>
		exportRows.mutate(
			{
				...filters,
				sort: sort?.key,
				dir: sort ? sort.direction : undefined,
			},
			{
				onSuccess: (ticket) =>
					toast('ok', `Downloading ${count(ticket.rowCount)} rows.`),
				onError: (error) =>
					toast(
						'danger',
						error instanceof ApiError
							? error.message
							: 'We couldn’t prepare the download. Try again.',
					),
			},
		);

	/* ── filter bar ── */
	const change = (key: string, value: string) => {
		setPage(1);
		if (key === 'q') setSearchText(value);
		else setChosen((current) => ({ ...current, [key]: value }));
	};
	const clearAll = () => {
		setPage(1);
		setSearchText('');
		setPlantText('');
		setDocumentText('');
		setChosen({
			classification: '',
			plantCode: '',
			documentType: '',
			periodStart: '',
			periodEnd: '',
		});
	};

	const searchableLabels = (summaryData?.searchable ?? []).map((key) =>
		labelOf(key).toLowerCase(),
	);
	const searchPlaceholder =
		searchableLabels.length === 0
			? undefined
			: searchableLabels.length <= 2
				? `Search ${searchableLabels.join(' or ')}`
				: `Search ${searchableLabels.slice(0, 2).join(', ')} and ${searchableLabels.length - 2} more`;

	const options = (
		values: { value: string; label?: string }[] | undefined,
		label = false,
	) =>
		(values ?? []).map((item) => ({
			value: item.value,
			label: label ? classLabel(item.value) : item.value,
		}));

	const specs: FilterSpec[] = [];
	if ((summaryData?.searchable.length ?? 0) > 0) {
		specs.push({
			key: 'q',
			label: 'Search',
			kind: 'search',
			placeholder: searchPlaceholder,
		});
	}
	if (filterable.has(CLASSIFICATION_KEY)) {
		specs.push({
			key: 'classification',
			label: 'Classification',
			kind: 'select',
			options: [
				{ value: '', label: 'All classifications' },
				...options(classValues.data?.values, true),
			],
		});
	}
	if (filterable.has('plant_code')) {
		specs.push({
			key: 'plantCode',
			label: labelOf('plant_code'),
			kind: 'combo',
			placeholder: plantValues.data?.truncated ? 'Type to search' : 'All',
			options: options(plantValues.data?.values),
			onInputChange: setPlantText,
		});
	}
	if (filterable.has('document_type')) {
		specs.push({
			key: 'documentType',
			label: labelOf('document_type'),
			kind: 'combo',
			placeholder: documentValues.data?.truncated
				? 'Type to search'
				: 'All',
			options: options(documentValues.data?.values),
			onInputChange: setDocumentText,
		});
	}
	if (filterable.has('invoice_date')) {
		const range = summaryData?.dateRange;
		specs.push(
			{
				key: 'periodStart',
				label: `${labelOf('invoice_date')} from`,
				kind: 'date',
				group: 'invoice-date',
				min: range?.min ?? undefined,
				max: chosen.periodEnd || range?.max || undefined,
			},
			{
				key: 'periodEnd',
				label: `${labelOf('invoice_date')} to`,
				kind: 'date',
				group: 'invoice-date',
				min: chosen.periodStart || range?.min || undefined,
				max: range?.max ?? undefined,
			},
		);
	}

	const filterValues: Record<string, string> = { q: searchText, ...chosen };
	const total = rows.data?.total;
	const from = total ? offset + 1 : 0;
	const to = total
		? Math.min(offset + (rows.data?.rows.length ?? 0), total)
		: 0;

	const chips: Array<{ key: string; label: string }> = [];
	if (q) chips.push({ key: 'q', label: `Search: “${q}”` });
	if (chosen.classification)
		chips.push({
			key: 'classification',
			label: `Classification: ${classLabel(chosen.classification)}`,
		});
	if (chosen.plantCode)
		chips.push({
			key: 'plantCode',
			label: `${labelOf('plant_code')}: ${chosen.plantCode}`,
		});
	if (chosen.documentType)
		chips.push({
			key: 'documentType',
			label: `${labelOf('document_type')}: ${chosen.documentType}`,
		});
	if (chosen.periodStart)
		chips.push({
			key: 'periodStart',
			label: `${labelOf('invoice_date')} from ${date(chosen.periodStart)}`,
		});
	if (chosen.periodEnd)
		chips.push({
			key: 'periodEnd',
			label: `${labelOf('invoice_date')} to ${date(chosen.periodEnd)}`,
		});

	/* ── states before the table ── */
	const notProcessed =
		!processed ||
		(summary.error instanceof ApiError && summary.error.status === 404);
	if (notProcessed) {
		return (
			<Card>
				<p className="small mut">
					This file has not been processed yet.
				</p>
			</Card>
		);
	}
	if (summary.error) {
		return (
			<EmptyState
				title="We couldn’t load the data."
				action={
					<Button
						variant="outlined"
						size="small"
						onClick={() => void summary.refetch()}
					>
						Try again
					</Button>
				}
			/>
		);
	}

	/* ── figures ── */
	const classEntries = summaryData?.byClassification
		? classificationEntries(summaryData.byClassification)
		: [];
	const totalRows = summaryData?.rowCount ?? 0;
	const unclassified = summaryData?.byClassification?.[UNCLASSIFIED] ?? 0;
	const mappedKnown =
		file.columnsMapped !== null && file.columnsTotal !== null;

	const figures = summaryData ? (
		<Figures>
			{/* On the audit side these are the rows locked into the audit, which can
          be fewer than the file holds — so the label says which. */}
			<Figure
				label={
					summaryData.source === 'audit' ? 'Rows in scope' : 'Rows'
				}
				value={count(totalRows)}
			/>
			{mappedKnown && (
				<Figure
					label="Columns mapped"
					value={`${count(file.columnsMapped)} / ${count(file.columnsTotal)}`}
				/>
			)}
			{classEntries.map(([key, value]) => (
				<Figure
					key={key}
					label={
						<StatusBadge group="classification" value={key} plain />
					}
					value={count(value)}
					tone={
						key === UNCLASSIFIED && unclassified > 0
							? 'warn'
							: undefined
					}
				/>
			))}
		</Figures>
	) : (
		<div className="mut small">Loading…</div>
	);

	return (
		<>
			{figures}

			<Card className="workbench">
				<Box display="flex" flexDirection="column" gap="var(--s3)">
					<FilterBar
						specs={specs}
						values={filterValues}
						onChange={change}
						onClear={clearAll}
						resultNote={
							total !== undefined
								? total === 0
									? 'No matching rows'
									: `Showing ${count(from)}–${count(to)} of ${count(total)}`
								: undefined
						}
						actions={
							<>
								{full.button}
								{summaryData && (
									<ColumnVisibility
										columns={allColumns.map((column) => ({
											key: column.key,
											label: labelOf(column.key),
										}))}
										visible={visible}
										defaultVisible={defaultVisible}
										onChange={setVisibleKeys}
									/>
								)}

								<Button
									size="small"
									variant="outlined"
									disabled={exportRows.isPending || !total}
									onClick={download}
								>
									{exportRows.isPending
										? /* Written on the server at roughly 800 rows a second, so a
                         big download is a minutes-long wait — say so up front. */
											(total ?? 0) > LARGE_DOWNLOAD_ROWS
											? 'Preparing… (a few minutes for large files)'
											: 'Preparing…'
										: filtering
											? 'Download filtered rows'
											: 'Download rows (Excel)'}
								</Button>
							</>
						}
					/>

					{chips.length > 0 && (
						<Box
							display="flex"
							flexWrap="wrap"
							alignItems="center"
							gap={1}
						>
							{chips.map((chip) => (
								<Chip
									key={chip.key}
									size="small"
									label={chip.label}
									onDelete={() => change(chip.key, '')}
								/>
							))}
						</Box>
					)}
				</Box>
			</Card>

			{/* A failed refetch keeps the last page on screen, so it must still say
          that the table no longer reflects what was asked for. */}
			{Boolean(rows.error) && rows.data && (
				<Note tone="danger" title="We couldn’t load the data." row>
					{rows.error instanceof ApiError
						? rows.error.message
						: 'The rows below are from before the last change.'}{' '}
					<Button
						size="small"
						variant="text"
						onClick={() => void rows.refetch()}
					>
						Try again
					</Button>
				</Note>
			)}
			{rows.error && !rows.data ? (
				<EmptyState
					title="We couldn’t load the data."
					action={
						<Button
							variant="outlined"
							size="small"
							onClick={() => void rows.refetch()}
						>
							Try again
						</Button>
					}
				/>
			) : (
				<div className="card">
					<div className="card-bd flush">
						<DataTable<PageRow>
							rows={(rows.data?.rows ?? []).map(
								(cells, index) => ({ index, cells }),
							)}
							columns={
								tableColumns.length
									? tableColumns
									: [
											{
												key: 'loading',
												label: '',
												render: () => null,
											},
										]
							}
							rowKey={(row) => String(offset + row.index)}
							caption={`${file.originalName} — rows`}
							fullScreen={full}
							loading={summary.isPending || rows.isPending}
							refreshing={rows.isFetching}
							onRowClick={(row) => setOpen(row)}
							sort={sort}
							onSortChange={(next) => {
								setSort(next);
								setPage(1);
							}}
							empty={
								filtering ? (
									<div>
										<p className="mut">
											No rows match these filters.
										</p>
										<Button
											size="small"
											variant="text"
											onClick={clearAll}
										>
											Clear filters
										</Button>
									</div>
								) : (
									<Empty title="This file has no rows." />
								)
							}
							paging={{
								page: rows.data?.page ?? page,
								pageSize: rows.data?.pageSize ?? pageSize,
								total: total ?? 0,
								pageSizes: PAGE_SIZES,
								onPageChange: setPage,
								onPageSizeChange: (size) => {
									setPageSize(size);
									setPage(1);
								},
							}}
						/>
					</div>
				</div>
			)}

			{between?.(summaryData)}

			<RowDrawer
				row={open}
				file={file}
				columns={summaryData?.columns ?? []}
				positionOf={positionOf}
				offset={offset}
				labelOf={labelOf}
				onClose={() => setOpen(null)}
			/>
		</>
	);
}

/* ═══ one row, in full ═════════════════════════════════════════════════════ */

function RowDrawer({
	row,
	file,
	columns,
	positionOf,
	labelOf,
	onClose,
}: {
	row: PageRow | null;
	file: FileRecord;
	columns: FileRowColumn[];
	positionOf: Map<string, number>;
	offset: number;
	labelOf: (key: string) => string;
	onClose: () => void;
}) {
	/* Kept while the drawer slides away, so it does not empty as it closes. */
	const [last, setLast] = useState<PageRow | null>(null);
	useEffect(() => {
		if (row) setLast(row);
	}, [row]);
	const shown = row ?? last;
	if (!shown) return null;

	const cellOf = (key: string) => shown.cells[positionOf.get(key) ?? -1];
	const sourceRow = cellOf(SOURCE_ROW_KEY);
	const classification = cellOf(CLASSIFICATION_KEY);

	const fieldColumns = columns.filter(
		(column) =>
			column.key !== HIDDEN_KEY &&
			column.key !== SOURCE_ROW_KEY &&
			column.key !== CLASSIFICATION_KEY,
	);
	const grouped = new Map<string, FileRowColumn[]>();
	let blank = 0;
	for (const column of fieldColumns) {
		if (isEmpty(cellOf(column.key))) {
			blank += 1;
			continue;
		}
		const group = groupOf(column.key, column.type);
		grouped.set(group, [...(grouped.get(group) ?? []), column]);
	}

	return (
		<SideDrawer
			open={Boolean(row)}
			onClose={onClose}
			title="Row details"
			subtitle={
				isEmpty(sourceRow)
					? file.originalName
					: `Row ${count(sourceRow)} in source file`
			}
		>
			<div className="stack">
				<section className="stack" style={{ gap: 'var(--s1)' }}>
					<h3 className="small b">Source</h3>
					<Detail label="Source file">{file.originalName}</Detail>
					{!isEmpty(sourceRow) && (
						<Detail label="Row in source file">
							{count(sourceRow)}
						</Detail>
					)}
				</section>

				{positionOf.has(CLASSIFICATION_KEY) && (
					<section className="stack" style={{ gap: 'var(--s1)' }}>
						<h3 className="small b">Classification</h3>
						<Detail label="Classification">
							<StatusBadge
								group="classification"
								value={
									isEmpty(classification)
										? UNCLASSIFIED
										: String(classification)
								}
							/>
						</Detail>
					</section>
				)}

				{GROUP_ORDER.filter((group) => grouped.has(group)).map(
					(group) => (
						<section
							key={group}
							className="stack"
							style={{ gap: 'var(--s1)' }}
						>
							<h3 className="small b">{group}</h3>
							{grouped.get(group)!.map((column) => (
								<Detail
									key={column.key}
									label={labelOf(column.key)}
								>
									{formatCell(
										cellOf(column.key),
										column.type,
									)}
								</Detail>
							))}
						</section>
					),
				)}

				{blank > 0 && (
					<p className="small mut">
						{blank === 1 ? '1 field has' : `${blank} fields have`}{' '}
						no value in this row.
					</p>
				)}
			</div>
		</SideDrawer>
	);
}
